import React, {
 useState
} from 'react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from 'shared/components/ui/dialog';
import {
 Button
} from 'shared/components/ui/button';
import {
 Input
} from 'shared/components/ui/input';
import {
    TriggerView, TriggerListItem
} from '../types/trigger.types';
import TriggersTable from '../components/TriggersTable';
import TriggerEditor from '../components/TriggerEditor';

let triggerIdCounter = 0;

const TriggersPage: React.FC = () => {
    const [triggers, setTriggers] = useState<TriggerListItem[]>([]);
    const [isLoading] = useState(false);
    const [view, setView] = useState<TriggerView>('list');
    const [editingTrigger, setEditingTrigger] = useState<TriggerListItem | null>(null);

    // Delete confirmation state
    const [deleteTarget, setDeleteTarget] = useState<TriggerListItem | null>(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');

    const handleSave = (data: Omit<TriggerListItem, 'id' | 'createdAt' | 'updatedAt' | 'lastFired'>) => {
        const now = new Date().toISOString().split('T')[0];

        if (editingTrigger) {
            setTriggers((prev) =>
                prev.map((t) =>
                    t.id === editingTrigger.id
                        ? {
 ...t,
...data,
updatedAt: now
}
                        : t
                )
            );
        } else {
            triggerIdCounter += 1;
            const newTrigger: TriggerListItem = {
                id: `trigger_${Date.now()}_${triggerIdCounter}`,
                ...data,
                lastFired: null,
                createdAt: now,
                updatedAt: now,
            };
            setTriggers((prev) => [...prev, newTrigger]);
        }

        setView('list');
        setEditingTrigger(null);
    };

    const handleEdit = (trigger: TriggerListItem) => {
        setEditingTrigger(trigger);
        setView('editor');
    };

    const handleDelete = () => {
        if (!deleteTarget) return;
        setTriggers((prev) => prev.filter((t) => t.id !== deleteTarget.id));
        setDeleteTarget(null);
        setDeleteConfirmText('');
    };

    const handleToggleActive = (triggerId: string) => {
        setTriggers((prev) =>
            prev.map((t) =>
                t.id === triggerId ? {
 ...t,
active: !t.active
} : t
            )
        );
    };

    const handleNewTrigger = () => {
        setEditingTrigger(null);
        setView('editor');
    };

    const handleEditorCancel = () => {
        setView('list');
        setEditingTrigger(null);
    };

    if (view === 'editor') {
        return (
            <TriggerEditor
                trigger={editingTrigger}
                onSave={handleSave}
                onCancel={handleEditorCancel}
            />
        );
    }

    return (
        <>
            <TriggersTable
                triggers={triggers}
                isLoading={isLoading}
                onEdit={handleEdit}
                onDelete={(t) => setDeleteTarget(t)}
                onToggleActive={handleToggleActive}
                onNewTrigger={handleNewTrigger}
            />

            {/* Delete confirmation dialog */}
            <Dialog
                open={!!deleteTarget}
                onOpenChange={(open) => {
                    if (!open) {
                        setDeleteTarget(null);
                        setDeleteConfirmText('');
                    }
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Trigger</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete the <span className="font-semibold">"{deleteTarget?.name}"</span> trigger?
                            This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-2">
                        <p className="text-sm text-muted-foreground mb-2">
                            Type <span className="font-semibold">{deleteTarget?.name}</span> to confirm:
                        </p>
                        <Input
                            placeholder="Type trigger name to confirm"
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setDeleteTarget(null); setDeleteConfirmText(''); }}>
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            disabled={deleteConfirmText !== deleteTarget?.name}
                            onClick={handleDelete}
                        >
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default TriggersPage;
